import { useEffect, useRef, useState } from "react";

// Same loose typo check the server enforces (ApplyForm, ChangeEmailModal use
// the identical pattern) — good enough here too; delivery is the real check.
const EMAIL_RE = /^\S+@\S+\.\S+$/;

/**
 * The "where should we send your analysis?" ask, INTEGRATED into the cost
 * step's pane (Rich, staging feedback 2026-07-04). This used to be a
 * full-screen gate (EmailConfirmGate) that REPLACED the cost content until
 * confirmed — but the step heading says "Review the cost" and the user saw
 * only an email form, with the pricing lede and cost cards hidden behind it.
 * Now it's a compact block the host renders ABOVE the cost content (pilot-
 * free band / PWYC cards / closed-checkout panel — all variants), in the
 * SAME pane, at the same time. Two shapes:
 *
 *   - prefilled (linkedEmail on file — an applicant-approved code or an
 *     operator hand-mint): a one-line confirm + a "Change" affordance. No
 *     button press needed to accept it — the host auto-confirms the linked
 *     address the instant it resolves (see App's auto-confirm effect next to
 *     `emailConfirmed`), so this component's onConfirm only fires from the
 *     (uncommon) override path: the user opens Change and saves a different
 *     address. Cancel discards the edit without calling onConfirm.
 *   - empty (self-serve, no address on file): a plain labeled input — no
 *     separate submit button. The step's own Continue/Authorize is the
 *     action; this calls onConfirm as soon as a valid address is typed, and
 *     onInvalid if it's cleared or mistyped, so Continue re-disables.
 *
 * Presentational: the host (App) decides what "confirmed" means — whether
 * the returned address differs from `linkedEmail` (an override, fed into
 * the existing `notifyEmail` state) or matches it (no override needed; the
 * code's linked address is already correct server-side).
 *
 * Density pass (Rich, staging feedback 2026-07-04): "takes up WAY too much
 * vertical space." Prefilled collapses to ONE line — confirm text with
 * "Change" inline, not a separate control. Empty drops the heading (the
 * question IS the field's label now) and trims the helper to the single
 * load-bearing safety clause.
 *
 * Prefilled ALSO covers a self-serve address already confirmed before this
 * mount (launch-audit fix, 2026-07-04): a Stripe Checkout round-trip is a
 * full-page reload that remounts this component from scratch, so a
 * self-serve code (no linkedEmail) that already had its address confirmed —
 * App's `notifyEmail`, restored from the pending-run stash — must not show a
 * blank ask again; it renders the SAME confirm-line + Change as a real
 * linkedEmail. `alreadyConfirmedSelfServe` captures this ONCE at mount
 * (`useState` initializer, never recomputed) so a first-time self-serve
 * typing session — where a valid address arrives keystroke-by-keystroke,
 * long after mount — never swaps the input out from under an in-progress
 * edit; only "confirmed before this component even existed" takes this path.
 */
export function EmailConfirmBlock({
  linkedEmail,
  address,
  onConfirm,
  onInvalid,
}: {
  linkedEmail: string | null;
  /** The address currently in effect (notifyEmail ?? linkedEmail), or null
   *  before the empty-case input holds a valid one. */
  address: string | null;
  onConfirm: (email: string) => void;
  onInvalid: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [selfServeInput, setSelfServeInput] = useState("");
  const [alreadyConfirmedSelfServe] = useState(() => !linkedEmail && !!address);
  // Collapse-on-blur (drill finding, 2026-07-22): a browser-autofilled
  // address is "confirmed" by onChange without a single keystroke, so the
  // user may never READ it — Rich's analysis went to an address he never
  // typed. Once the field holds a valid address and focus leaves it, the
  // input becomes the same read-back sentence the prefilled shape uses
  // ("We'll send your analysis to X — Change"): the form-field-turns-into-
  // asserted-fact transition is what draws the eye. Presentational only —
  // confirmation semantics (onConfirm/onInvalid from onChange) are
  // unchanged. Never collapses mid-typing: blur is the only trigger — and
  // the collapse is DEFERRED 200ms past blur (review finding, 2026-07-22):
  // the settled line is ~59px shorter than the input shape, so collapsing
  // synchronously on blur moved the footer Continue upward between a
  // click's mousedown (which fires the blur) and its mouseup — eating the
  // very click the user aimed at it. A click's down→up spans ~100ms;
  // deferring the layout shift past that lets the click land where it was
  // pressed. Refocusing within the window cancels the pending collapse.
  const [settled, setSettled] = useState(false);
  const settleTimer = useRef<number | null>(null);
  const cancelPendingSettle = () => {
    if (settleTimer.current !== null) {
      window.clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
  };
  useEffect(() => cancelPendingSettle, []);

  // Prefilled — a linked address is on file, OR a self-serve address was
  // already confirmed before this mount.
  if (linkedEmail || alreadyConfirmedSelfServe) {
    // Unreachable "" fallback: this branch is entered only when linkedEmail
    // or address (that's what alreadyConfirmedSelfServe captured) is truthy
    // — TS just can't see that guarantee across the two conditions.
    const shown = address ?? linkedEmail ?? "";
    if (!editing) {
      return (
        <div className="email-confirm-block email-confirm-block-prefilled">
          <p className="email-confirm-line">
            We&rsquo;ll send your analysis to <strong>{shown}</strong>
            {" — "}
            <button
              type="button"
              className="email-confirm-change"
              onClick={() => {
                setDraft(shown);
                setEditing(true);
              }}
            >
              Change
            </button>
          </p>
        </div>
      );
    }
    const trimmedDraft = draft.trim();
    const draftValid = EMAIL_RE.test(trimmedDraft);
    return (
      <div className="email-confirm-block email-confirm-block-editing">
        <label className="cem-field email-confirm-field">
          <span>Email</span>
          <input
            type="email"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && draftValid) {
                onConfirm(trimmedDraft);
                setEditing(false);
              }
            }}
          />
        </label>
        <div className="email-confirm-actions">
          <button
            type="button"
            className="cem-btn ghost"
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cem-btn solid"
            disabled={!draftValid}
            onClick={() => {
              onConfirm(trimmedDraft);
              setEditing(false);
            }}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  // Self-serve address settled (valid + blurred): the same read-back line
  // the prefilled shape renders, with Change reopening the input in place.
  const settledAddress = selfServeInput.trim();
  if (settled && EMAIL_RE.test(settledAddress)) {
    return (
      <div className="email-confirm-block email-confirm-block-prefilled">
        <p className="email-confirm-line">
          We&rsquo;ll send your analysis to <strong>{settledAddress}</strong>
          {" — "}
          <button
            type="button"
            className="email-confirm-change"
            onClick={() => setSettled(false)}
          >
            Change
          </button>
        </p>
      </div>
    );
  }

  // Empty — self-serve, no address on file. Two rows: label+input, helper.
  return (
    <div className="email-confirm-block email-confirm-block-empty">
      <label className="cem-field email-confirm-field">
        <span>Where should we send your analysis?</span>
        <input
          type="email"
          autoFocus
          value={selfServeInput}
          placeholder="you@example.com"
          onChange={(e) => {
            const value = e.target.value;
            setSelfServeInput(value);
            const trimmed = value.trim();
            if (EMAIL_RE.test(trimmed)) {
              onConfirm(trimmed);
            } else {
              onInvalid();
            }
          }}
          onFocus={cancelPendingSettle}
          onBlur={() => {
            if (EMAIL_RE.test(selfServeInput.trim())) {
              cancelPendingSettle();
              settleTimer.current = window.setTimeout(() => {
                settleTimer.current = null;
                setSettled(true);
              }, 200);
            }
          }}
        />
      </label>
      <p className="email-confirm-helper">
        Make sure it&rsquo;s an inbox only you can open.
      </p>
    </div>
  );
}
